/**
 * @file     gating.ts
 * @readme   ./README.md
 * @purpose  Lesson gating logic, determines whether a lesson requires auth and is locked for the current user.
 */

import type { AudienceId, JourneyConfig, Lesson } from './types';

interface GateResult {
  locked: boolean;
  reason: 'none' | 'auth-required';
}

/**
 * Free content rule:
 * - All lessons in Module 1 of each track are free
 * - First lesson of Module 2 is free (taste)
 * - Everything else requires an account
 */
export function isLessonFree(journey: JourneyConfig, moduleIndex: number, lessonIndex: number): boolean {
  if (moduleIndex === 0) return true;
  if (moduleIndex === 1 && lessonIndex === 0) return true;
  return false;
}

export function isLessonLocked(
  journey: JourneyConfig,
  moduleIndex: number,
  lessonIndex: number,
  isAuthenticated: boolean,
): GateResult {
  if (isLessonFree(journey, moduleIndex, lessonIndex)) {
    return { locked: false, reason: 'none' };
  }

  if (!isAuthenticated) {
    return { locked: true, reason: 'auth-required' };
  }

  return { locked: false, reason: 'none' };
}

export function findLessonIndices(
  journey: JourneyConfig,
  moduleSlug: string,
  lessonSlug: string,
): { moduleIndex: number; lessonIndex: number; module: JourneyConfig['modules'][0]; lesson: Lesson } | null {
  const moduleIndex = journey.modules.findIndex((m) => m.slug === moduleSlug);
  if (moduleIndex === -1) return null;
  const mod = journey.modules[moduleIndex];
  const lessonIndex = mod.lessons.findIndex((l) => l.slug === lessonSlug);
  if (lessonIndex === -1) return null;
  return { moduleIndex, lessonIndex, module: mod, lesson: mod.lessons[lessonIndex] };
}
